/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Play,
  Settings,
  Square,
  TestTube,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { SettingsSection } from '../components/settings-section'
import { api } from '@/lib/api'

type AcceleratorStatus = {
  installed: boolean
  running: boolean
  pid?: number
  status_text?: string
  last_error?: string
  binary_path?: string
  raw_output?: string
}

export function AcceleratorSection() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<AcceleratorStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [binaryPath, setBinaryPath] = useState('')

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/accelerator/status')
      if (res.data?.success) {
        setStatus(res.data.data)
        if (res.data.data.binary_path) {
          setBinaryPath(res.data.data.binary_path)
        }
      }
    } catch {
      setStatus({ installed: false, running: false })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    const interval = setInterval(fetchStatus, 10000)
    return () => clearInterval(interval)
  }, [fetchStatus])

  const handleAction = async (action: string) => {
    setActionLoading(action)
    try {
      const res = await api.post(`/api/accelerator/${action}`)
      if (res.data?.success) {
        toast.success(t(res.data.message))
      } else {
        toast.error(t(res.data.message || 'Operation failed'))
      }
      await fetchStatus()
    } catch (err: any) {
      toast.error(t(err.response?.data?.message || 'Request failed'))
    } finally {
      setActionLoading(null)
    }
  }

  const handleUpdatePath = async () => {
    if (!binaryPath.trim()) {
      toast.error(t('Please enter the binary path'))
      return
    }
    setActionLoading('path')
    try {
      const res = await api.put('/api/accelerator/path', {
        path: binaryPath,
      })
      if (res.data?.success) {
        toast.success(t(res.data.message))
      } else {
        toast.error(t(res.data.message || 'Update failed'))
      }
      await fetchStatus()
    } catch (err: any) {
      toast.error(t(err.response?.data?.message || 'Request failed'))
    } finally {
      setActionLoading(null)
    }
  }

  const handleTestConnection = async () => {
    setActionLoading('test')
    try {
      const res = await api.get('/api/accelerator/test')
      if (res.data?.success) {
        toast.success(t(res.data.message))
      } else {
        toast.error(t(res.data.message || 'Connection test failed'))
      }
    } catch (err: any) {
      toast.error(t(err.response?.data?.message || 'Request failed'))
    } finally {
      setActionLoading(null)
    }
  }

  const isLoading = (action: string) => actionLoading === action

  return (
    <SettingsSection
      title={t('LinuxDO Accelerator')}
      description={t(
        'Manage the LinuxDO network accelerator to bypass SNI blocking'
      )}
    >
      <div className='space-y-6'>
        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Activity className='h-5 w-5' />
              {t('Accelerator Status')}
            </CardTitle>
            <CardDescription>
              {t('Current status of the LinuxDO Accelerator service')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className='flex items-center gap-2 text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                {t('Loading status...')}
              </div>
            ) : status?.installed ? (
              <div className='space-y-4'>
                <div className='flex items-center gap-3'>
                  <Badge
                    variant={status.running ? 'default' : 'secondary'}
                    className={
                      status.running
                        ? 'bg-green-500/10 text-green-500 border-green-500/20'
                        : ''
                    }
                  >
                    {status.running ? (
                      <CheckCircle2 className='h-3 w-3 mr-1' />
                    ) : (
                      <AlertCircle className='h-3 w-3 mr-1' />
                    )}
                    {status.running ? t('Running') : t('Stopped')}
                  </Badge>
                  {status.pid && (
                    <span className='text-sm text-muted-foreground'>
                      PID: {status.pid}
                    </span>
                  )}
                </div>

                {status.binary_path && (
                  <p className='text-sm text-muted-foreground'>
                    {t('Binary')}: {status.binary_path}
                  </p>
                )}

                {status.last_error && (
                  <p className='text-sm text-destructive'>
                    {t('Error')}: {status.last_error}
                  </p>
                )}

                <Separator />

                <div className='flex flex-wrap gap-2'>
                  {!status.running ? (
                    <Button
                      size='sm'
                      onClick={() => handleAction('start')}
                      disabled={isLoading('start')}
                    >
                      {isLoading('start') ? (
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      ) : (
                        <Play className='h-4 w-4 mr-2' />
                      )}
                      {t('Start')}
                    </Button>
                  ) : (
                    <Button
                      size='sm'
                      variant='destructive'
                      onClick={() => handleAction('stop')}
                      disabled={isLoading('stop')}
                    >
                      {isLoading('stop') ? (
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      ) : (
                        <Square className='h-4 w-4 mr-2' />
                      )}
                      {t('Stop')}
                    </Button>
                  )}

                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => handleAction('setup')}
                    disabled={isLoading('setup')}
                  >
                    {isLoading('setup') ? (
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    ) : (
                      <Settings className='h-4 w-4 mr-2' />
                    )}
                    {t('Setup')}
                  </Button>

                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => handleAction('cleanup')}
                    disabled={isLoading('cleanup')}
                  >
                    {isLoading('cleanup') ? (
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    ) : (
                      <Trash2 className='h-4 w-4 mr-2' />
                    )}
                    {t('Cleanup')}
                  </Button>

                  <Button
                    size='sm'
                    variant='outline'
                    onClick={handleTestConnection}
                    disabled={isLoading('test')}
                  >
                    {isLoading('test') ? (
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    ) : (
                      <TestTube className='h-4 w-4 mr-2' />
                    )}
                    {t('Test Connection')}
                  </Button>

                  <Button
                    size='sm'
                    variant='ghost'
                    onClick={fetchStatus}
                    disabled={loading}
                  >
                    {t('Refresh')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className='space-y-4'>
                <p className='text-muted-foreground'>
                  {t(
                    'LinuxDO Accelerator is not installed or not found on this system'
                  )}
                </p>
                <p className='text-sm text-muted-foreground'>
                  {t(
                    'Please install the accelerator binary and configure the path below'
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Settings className='h-5 w-5' />
              {t('Configuration')}
            </CardTitle>
            <CardDescription>
              {t('Configure the accelerator binary path')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='binary-path'>{t('Binary Path')}</Label>
                <div className='flex gap-2'>
                  <Input
                    id='binary-path'
                    placeholder='D:\Program Files\Linux.do Accelerator\linuxdo-accelerator.exe'
                    value={binaryPath}
                    onChange={(e) => setBinaryPath(e.target.value)}
                  />
                  <Button
                    onClick={handleUpdatePath}
                    disabled={isLoading('path')}
                  >
                    {isLoading('path') ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      t('Save')
                    )}
                  </Button>
                </div>
                <p className='text-sm text-muted-foreground'>
                  {t(
                    'Path to the linuxdo-accelerator executable. On Windows, typically in Program Files.'
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('How it works')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 text-sm text-muted-foreground'>
            <p>
              {t(
                'The LinuxDO Accelerator uses ECH (Encrypted Client Hello) encryption to bypass SNI blocking by ISPs.'
              )}
            </p>
            <p>
              {t(
                'It works by installing a local root certificate, modifying the hosts file, and running a local proxy on 127.211.73.84.'
              )}
            </p>
            <p>
              {t(
                'When enabled, the system will route LinuxDO OAuth traffic through the accelerator, allowing users to log in even when linux.do is blocked.'
              )}
            </p>
            <p className='font-medium text-foreground'>
              {t('Steps:')}
            </p>
            <ol className='list-decimal list-inside space-y-1'>
              <li>{t('Install the LinuxDO Accelerator binary')}</li>
              <li>{t('Configure the binary path above')}</li>
              <li>{t('Click "Setup" to install certificates and configure hosts')}</li>
              <li>{t('Click "Start" to run the accelerator')}</li>
              <li>{t('Enable the accelerator in the OAuth settings')}</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </SettingsSection>
  )
}
